import React, { useState, useEffect, useLayoutEffect, lazy, Suspense } from "react";
import { HashRouter, Routes, Route, Navigate, useParams, useSearchParams, useLocation, useNavigationType } from "react-router-dom";
import { Preferences } from '@capacitor/preferences';
import ClubRoots from "./assets/clubRoots.svg";
import PrivateRoute from "./PrivateRoute";
import { useAuth } from "./utils/AuthContext";
import OfflineBanner from './controls/OfflineBanner';
import { useDeepLinks } from './utils/useDeepLinks';
import { hasRecentJourneyNavigationSignal, recordJourneyPage, toJourneyRoute } from './utils/journeyTracking';
import { watchJourneyLifecycle } from './utils/journeyLifecycle';
import { redirectState } from './utils/routeNavigation';
import JourneyRendered from './controls/JourneyRendered';

const DeepLinkHandler = () => {
  useDeepLinks();
  return null;
};

/** Preserves query params (e.g. UTM tags) across the /demo -> /team/demo/home redirect. */
const DemoRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/team/demo/home${location.search}`} replace state={redirectState("demo")} />;
};

const Teams = lazy(() => import("./pages/Teams"));
const TeamHome = lazy(() => import("./pages/TeamHome"));
const ClubHome = lazy(() => import("./pages/ClubHome"));
const Login = lazy(() => import("./pages/Login"));
const DiscountPaymentPage = lazy(() => import("./pages/DiscountPaymentPage"));
const PrivacyPolicyRoute = lazy(() => import("./pages/PrivacyPolicyRoute"));
const DiaryAttendanceResponsePage = lazy(() => import("./pages/DiaryAttendanceResponsePage"));
// Lazy load ScoutPortal (contains ag-grid)
const ScoutPortal = lazy(() => import("./pages/ScoutPortal"));

const TEAM_ROUTE_TABS = [
  'home', 'players', 'results', 'info', 'stats', 'lookup', 'blog',
  'fixtures', 'news', 'settings', 'followRequests', 'supporters', 'options', 'detailSettings',
  'teamBuilder', 'playerTimers', 'tournamentTracker', 'gamePlanner', 'chat', 'payments', 'tacticalVideos', 'tacticsBoard', 'trainingPlans', 'playerIntel', 'roles', 'ai'
] as const;

const CLUB_ROUTE_TABS = [
  'home', 'fixtures', 'results', 'teams', 'blog', 'links', 'about', 'news', 'payments', 'bookings', 'scouts', 'settings'
] as const;

const TEAMS_TAB_INDEX: Record<string, number> = {
  welcome: 0,
  list: 1,
  clubs: 2,
  signup: 3,
  tools: 4,
  guidedhelp: 5,
  audit: 6,
  scouts: 7,
  help: 8,
  news: 9,
};

const DEFAULT_TITLE = "ClubRoots";
const DEFAULT_DESCRIPTION = "ClubRoots: Season fixtures, results and stats";

const setDocumentMetadata = (title: string, description: string) => {
  document.title = title;
  const metaDescription = document.querySelector('meta[name="description"]') || (() => {
    const tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
    return tag;
  })();
  metaDescription.setAttribute('content', description);
};

const getTeamLabel = (teamSlug: string) => {
  return decodeURIComponent(teamSlug).replaceAll('_', ' ').trim();
};

const getTeamSectionLabel = (section: string) => {
  const map: Record<string, string> = {
    home: 'Home',
    players: 'Players',
    results: 'Results',
    info: 'Info',
    stats: 'Stats',
    lookup: 'Lookup',
    blog: 'Blog',
    fixtures: 'Fixtures',
    news: 'News',
    settings: 'Settings',
    followrequests: 'Follow Requests',
    supporters: 'Supporters',
    options: 'Options',
    detailsettings: 'Detail Settings',
    teambuilder: 'Team Builder',
    playertimers: 'Player Timers',
    gameplanner: 'Game Time Planner',
    tournamenttracker: 'Tournament Tracker',
    chat: 'Chat',
    tacticalvideos: 'Tactical Videos',
    tacticsboard: 'Tactics Board',
    trainingplans: 'AI Training',
    ai: 'AI',
    playerintel: 'Spotlight',
    roles: 'Roles',
  };
  return map[section] ?? 'Home';
};

const getTeamsSectionLabel = (section: string | undefined) => {
  if (!section) return 'Teams';
  const map: Record<string, string> = {
    welcome: 'Welcome',
    list: 'Teams',
    clubs: 'Clubs',
    signup: 'Sign Up',
    tools: 'Match Tools',
    guidedhelp: 'Guided Help',
    audit: 'Audit Log',
    scouts: 'Scout Portal',
    help: 'Help',
    news: 'News'
  };
  return map[section] ?? 'Teams';
};

const getMetadataForPath = (pathname: string) => {
  const segments = pathname.split('?')[0].split('/').filter(Boolean);
  if (segments.length === 0) {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
  }

  if (segments[0] === 'login') {
    return { title: `${DEFAULT_TITLE} | Sign In`, description: 'Sign in to manage your ClubRoots team.' };
  }

  if (segments[0] === 'signup') {
    return { title: `${DEFAULT_TITLE} | Sign Up`, description: 'Create your ClubRoots account and team.' };
  }

  if (segments[0] === 'help' && segments[1] === 'privacy') {
    return {
      title: `${DEFAULT_TITLE} | Privacy Policy`,
      description: 'Read the ClubRoots privacy policy for data handling, retention, and user rights.'
    };
  }

  if (segments[0] === 'help') {
    return { title: `${DEFAULT_TITLE} | Help`, description: 'Get support, raise tickets, and access guided help.' };
  }

  if (segments[0] === 'scouts') {
    return { title: `${DEFAULT_TITLE} | Scout Portal`, description: 'Scout portal for verified access to player data.' };
  }

  if (segments[0] === 'tools') {
    const toolMap: Record<string, string> = {
      gameplanner: 'Game Time Planner',
      trainingplans: 'Training Plans',
      timers: 'Player Timers',
      squad: 'Squad Builder',
      tournament: 'Tournament Tracker',
    };
    const toolLabel = segments[1] ? (toolMap[segments[1]] ?? 'Match Tools') : 'Match Tools';
    return { title: `${DEFAULT_TITLE} | ${toolLabel}`, description: `${toolLabel} — free match tools, no sign-up needed.` };
  }

  if (segments[0] === 'teams') {
    const sectionLabel = getTeamsSectionLabel(segments[1]);
    return { title: `${DEFAULT_TITLE} | ${sectionLabel}`, description: 'Browse teams, sign up, and manage ClubRoots access.' };
  }

  if (segments[0] === 'clubs') {
    return { title: `${DEFAULT_TITLE} | Clubs`, description: 'Browse clubs on ClubRoots.' };
  }

  if (segments[0] === 'team') {
    const teamName = segments[1] ? getTeamLabel(segments[1]) : 'Team';
    const sectionLabel = getTeamSectionLabel(segments[2]?.toLowerCase() ?? 'home');
    return {
      title: `${DEFAULT_TITLE} | ${teamName} - ${sectionLabel}`,
      description: `${sectionLabel} for ${teamName} on ClubRoots.`
    };
  }

  if (segments[0] === 'club') {
    const clubName = segments[1] ? getTeamLabel(segments[1]) : 'Club';
    const sectionLabel = segments[2] ? (segments[2].charAt(0).toUpperCase() + segments[2].slice(1)) : 'Home';
    return {
      title: `${DEFAULT_TITLE} | ${clubName} - ${sectionLabel}`,
      description: `${sectionLabel} for ${clubName} club on ClubRoots.`
    };
  }

  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
};

const MetadataManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const { title, description } = getMetadataForPath(location.pathname);
    setDocumentMetadata(title, description);
  }, [location.pathname]);

  return null;
};

/** Logs signed-in users' page journeys (fire-and-forget, batched). */
const JourneyTracker: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const uid = user && !user.isAnonymous && user.email !== import.meta.env.VITE_FIREBASE_CUSTOM_IDENTIFIER
    ? user.uid : undefined;
  const navigationType = useNavigationType();
  const previousRef = React.useRef<{ route: string; key: string; uid?: string } | null>(null);
  const locationRef = React.useRef(location);
  locationRef.current = location;

  useLayoutEffect(() => {
    const route = toJourneyRoute(location.pathname, location.search);
    const previous = previousRef.current;
    const authChanged = previous?.key === location.key && previous.uid !== uid;
    const reason = navigationType !== "POP" && typeof location.state?.journeyRedirect === "string"
      ? location.state.journeyRedirect : undefined;
    const source = authChanged ? "auth_state" : reason ? "redirect" :
      hasRecentJourneyNavigationSignal() ? "user" :
        !previous ? "entry" : navigationType === "POP" ? "history" : "unknown";
    recordJourneyPage(uid, location.pathname, location.search, {
      route, previousRoute: previous?.route, source, navigationType, redirect: reason,
    });
    previousRef.current = { route, key: location.key, uid };
  }, [uid, location.pathname, location.search, location.key, navigationType]);

  useEffect(() => {
    if (!uid) return;
    return watchJourneyLifecycle(() => ({
      uid, pathname: locationRef.current.pathname, search: locationRef.current.search,
    }));
  }, [uid]);

  return null;
};

// Wrapper to extract team name from URL and pass as prop
const TeamHomeWrapper: React.FC<{ splashScreen: React.ReactNode, isAuthenticated: boolean, splashDone: boolean, overrideTeamName?: string }> = ({ splashScreen, isAuthenticated, splashDone, overrideTeamName }) => {
  const { teamName: paramTeamName, path, tag } = useParams<{ teamName: string, path: string, tag: string, param: string }>();
  const teamName = overrideTeamName ?? paramTeamName;
  let selectedTab: typeof TEAM_ROUTE_TABS[number] | undefined = undefined;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const tab = searchParams.get("tab"); // e.g. ?myParam=value
  const { user } = useAuth();
  if (path) {
    const normalized = path.toLowerCase();
    // Special handling for result URLs (e.g., result-20260131-TeamName)
    if (normalized.startsWith('result-')) {
      selectedTab = 'home';
    } else {
      selectedTab = TEAM_ROUTE_TABS.find(
        (teamTab) => teamTab.toLowerCase() === normalized
      );
    }
  }
  // Default to home if no tab is determined
  if (selectedTab === undefined) {
    selectedTab = 'home';
  }
  // Demo team bypasses authentication
  const isDemoTeam = teamName?.toLowerCase() === 'demo';
  // Show splash screen until authenticated and splashDone (unless demo team)
  if (!isDemoTeam && (!isAuthenticated || !splashDone)) {
    return <>{splashScreen}</>;
  }
  if (selectedTab === 'stats' && tab === 'ai') {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'analysis');
    const archivePath = tag ? `/${tag}` : '';
    return <Navigate to={`/team/${teamName}/ai${archivePath}?${params.toString()}`} replace state={redirectState("legacy_ai", location.state)} />;
  }
  return <div>
    <Suspense fallback={<div>Loading...</div>}>
      {/* key forces a full remount when the team route param changes (e.g. tapping a
          notification for a different team) — TeamHome only resolves/subscribes on mount */}
      <TeamHome key={`${teamName}:${user?.uid ?? 'signed-out'}`} selectedTeam={teamName?.replaceAll('_', ' ') || ""} selectedPath={selectedTab} selectedTag={tag} pathTab={tab} />
    </Suspense>
  </div>
};

// Wrapper to extract club slug from URL and pass as prop
const ClubHomeWrapper: React.FC = () => {
  const { clubSlug, path } = useParams<{ clubSlug: string; path: string }>();
  let selectedTab: string | undefined;
  if (path) {
    const normalized = path.toLowerCase();
    if (CLUB_ROUTE_TABS.includes(normalized as typeof CLUB_ROUTE_TABS[number])) {
      selectedTab = normalized;
    }
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ClubHome clubSlug={clubSlug ?? ''} selectedTab={selectedTab as any} />
      <JourneyRendered />
    </Suspense>
  );
};

const SignupWrapper: React.FC = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type')?.toLowerCase();
  const defaultAddSubTab = type === 'club' ? 'club' : 'team';
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Teams defaultTab={TEAMS_TAB_INDEX.signup as any} defaultAddSubTab={defaultAddSubTab} />
      <JourneyRendered />
    </Suspense>
  );
};

// Wrapper to extract team name from URL and pass as prop
const TeamsHomeWrapper: React.FC = () => {
  const { path } = useParams<{ path: string }>();
  let selectedTab: number | undefined = undefined;
  if (path) {
    const normalized = path.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(TEAMS_TAB_INDEX, normalized)) {
      selectedTab = TEAMS_TAB_INDEX[normalized];
    }
  }
  return <div>
    <Suspense fallback={<div>Loading...</div>}>
      <Teams defaultTab={selectedTab as any} />
      <JourneyRendered />
    </Suspense>
  </div>
};

const Router: React.FC = () => {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedTeamHydrated, setSelectedTeamHydrated] = useState(false);
  const [splashDone, setSplashDone] = useState(() => {
    try {
      return sessionStorage.getItem('splashShown') === 'true';
    } catch (error) {
      // sessionStorage might not be available in some contexts
      console.warn('sessionStorage not available:', error);
      return false;
    }
  });

  // Load selected team from Preferences on mount
  useEffect(() => {
    Preferences.get({ key: 'SelectedTeam' })
      .then(({ value }) => {
        if (value) {
          // Backward-compatible normalization for legacy stored values.
          const normalized = value.toLowerCase().replaceAll(' ', '_').trim();
          setSelectedTeam(normalized);
        }
      })
      .catch(error => console.error("Failed to restore selected team:", error))
      .finally(() => setSelectedTeamHydrated(true));
  }, []);

  useEffect(() => {
    if (splashDone) return;
    const timer = setTimeout(() => {
      setSplashDone(true);
      try {
        sessionStorage.setItem('splashShown', 'true');
      } catch (error) {
        // Ignore sessionStorage errors - splash state still works via state
        console.warn('Could not save splash state to sessionStorage:', error);
      }
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [splashDone]);

  const splashScreen = (
    <div
      style={{
        background: "#fff",
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img src={ClubRoots} alt="ClubRoots" style={{ width: "50vw", height: "50vh", maxWidth: 400, maxHeight: 400 }} />
    </div>
  );
  const { user } = useAuth();
  return (
    <HashRouter>
      <MetadataManager />
      <JourneyTracker />
      <DeepLinkHandler />
      <OfflineBanner />
      <Routes>
        <Route
          path="/"
          element={
            !selectedTeamHydrated
              ? splashScreen
              : selectedTeam
                ? <Navigate to={`/team/${selectedTeam}`} replace state={redirectState("root_saved_team")} />
                : <Navigate to="/teams" replace state={redirectState("root_welcome")} />
          }
        />
        <Route path="/teams" element={<TeamsHomeWrapper />} />
        <Route path="/teams/:path" element={<TeamsHomeWrapper />} />
        <Route
          path="/clubs/list"
          element={<Suspense fallback={<div>Loading...</div>}><Teams defaultTab={TEAMS_TAB_INDEX.list as any} defaultListSubTab="clubs" /><JourneyRendered /></Suspense>}
        />
        <Route path="/demo"
          element={<DemoRedirect />}
        />
        <Route path="/signup" element={<SignupWrapper />} />
        <Route path="/diary-attendance/:requestId" element={
          <PrivateRoute>
            <Suspense fallback={<div>Loading...</div>}><DiaryAttendanceResponsePage /><JourneyRendered /></Suspense>
          </PrivateRoute>
        } />
        {/* Demo team routes — public, no auth required */}
        <Route
          path="/team/demo/:path/:tag"
          element={<TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={true} splashDone={true} overrideTeamName="demo" />}
        />
        <Route
          path="/team/demo/:path"
          element={<TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={true} splashDone={true} overrideTeamName="demo" />}
        />
        <Route
          path="/team/demo"
          element={<TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={true} splashDone={true} overrideTeamName="demo" />}
        />
        {/* Non-demo team routes, including shared results, require authentication. */}
        <Route
          path="/team/:teamName/:path/:tag"
          element={<PrivateRoute><TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={user !== null} splashDone={splashDone} /></PrivateRoute>}
        />
        <Route
          path="/team/:teamName/:path"
          element={<PrivateRoute><TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={user !== null} splashDone={splashDone} /></PrivateRoute>}
        />
        <Route
          path="/team/:teamName"
          element={<PrivateRoute><TeamHomeWrapper splashScreen={splashScreen} isAuthenticated={user !== null} splashDone={splashDone} /></PrivateRoute>}
        />
        {/* Club routes — public, no auth required */}
        <Route path="/club/:clubSlug" element={<ClubHomeWrapper />} />
        <Route path="/club/:clubSlug/:path" element={<ClubHomeWrapper />} />
        {/* Discount payment link — requires auth (redirected to login if not) */}
        <Route path="/pay/:token" element={<Suspense fallback={<div>Loading...</div>}><DiscountPaymentPage /><JourneyRendered /></Suspense>} />
        {/* Public match tools routes — show full Teams navbar */}
        <Route path="/tools" element={<Suspense fallback={<div>Loading...</div>}><Teams defaultTab={TEAMS_TAB_INDEX.tools as any} /><JourneyRendered /></Suspense>} />
        <Route path="/tools/:tool" element={<Suspense fallback={<div>Loading...</div>}><Teams defaultTab={TEAMS_TAB_INDEX.tools as any} /><JourneyRendered /></Suspense>} />
        <Route path="/login" element={<Suspense fallback={<div>Loading...</div>}><Login /><JourneyRendered /></Suspense>} />
        <Route path="/help/privacy" element={<Suspense fallback={<div>Loading...</div>}><PrivacyPolicyRoute /><JourneyRendered /></Suspense>} />
        <Route path="/scouts" element={
          <PrivateRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ScoutPortal />
              <JourneyRendered />
            </Suspense>
          </PrivateRoute>
        } />
        <Route path="/help" element={<Suspense fallback={<div>Loading...</div>}><Teams defaultTab={TEAMS_TAB_INDEX.help as any} /><JourneyRendered /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace state={redirectState("unknown_route")} />} />
      </Routes>
    </HashRouter>
  )
}

export default Router;